import { useState, useEffect, useRef } from 'react';
import { protocolDays, ProtocolDayData } from '@/data/mockData';
import { Check, Lock, ChevronRight, Loader2, Sparkles, Trophy, Target, ArrowLeft, Info, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import confetti from 'canvas-confetti';
import { useAuth } from '@/contexts/AuthContext';

// Helper for Personalization
const getPersonalizationFocus = (day: ProtocolDayData) => {
  // In a real app, we would read from user's onboarding quiz
  // Since the quiz was removed in a previous step, we leave the logic prepared:
  const symptoms = localStorage.getItem('user_symptoms') || 'hinchazon'; 
  
  if (symptoms.includes('hinchazon') && day.title.includes('Hidratación')) {
    return "💡 Para la hinchazón: Beber despacio mejora aún más los resultados.";
  }
  if (symptoms.includes('estres') && day.title.includes('Emocional')) {
    return "💡 Para el estrés: Respira 3 veces antes de cada comida.";
  }
  return null;
};

const Protocol = () => {
  const { user } = useAuth();
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  
  // States for completion
  const [completedDays, setCompletedDays] = useState<Set<number>>(new Set());
  const [detailedTasks, setDetailedTasks] = useState<Record<string, boolean>>({});
  const [isResetCompleted, setIsResetCompleted] = useState<boolean>(false);
  const [resetLoading, setResetLoading] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const celebratedDays = useRef<Set<number>>(new Set());

  // Dynamically calculate unlocked days
  const maxCompleted = completedDays.size > 0 ? Math.max(...Array.from(completedDays)) : -1;
  const unlockedDays = maxCompleted + 2; 
  const currentPhase = Math.min(3, Math.ceil(unlockedDays / 7));

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user) return;
      
      // Load detailed tasks from local storage
      const savedTasks = localStorage.getItem(`protocol_tasks_${user.id}`);
      if (savedTasks) {
        setDetailedTasks(JSON.parse(savedTasks));
      }

      try {
        // Fetch profile data to check reset status
        const profileRes = await fetch('/api/profile/data');
        const profileData = await profileRes.json();
        if (profileData.data) {
          setIsResetCompleted(profileData.data.reset_completed || false);
        }

        // Load completed days via specialized proxy
        const res = await fetch('/api/protocol/status');
        const { data } = await res.json();
          
        if (data) {
          const cDays = new Set<number>();
          data.forEach((row: any) => {
            if (row.completed) {
              cDays.add(row.day_number);
              celebratedDays.current.add(row.day_number);
            }
          });
          setCompletedDays(cDays);
        }
      } catch (err) {
        console.error("Error fetching progress:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [user]);

  const handleCompleteReset = async () => {
    if (!user) return;
    setResetLoading(true);
    try {
      const res = await fetch('/api/profile/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: { reset_completed: true }
        })
      });
      const result = await res.json();

      if (!res.ok) {
        console.error("Reset API error:", result);
        toast({ 
          title: "Error al guardar", 
          description: result.error || "No se pudo guardar el estado del reset. Intenta de nuevo.", 
          variant: "destructive" 
        });
        return;
      }

      if (result.success) {
        setIsResetCompleted(true);
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
        toast({ title: "¡Fase 1 Desbloqueada!", description: "Has completado el Reset de 3 días. Ahora puedes iniciar el protocolo." });
      } else {
        console.error("Reset returned no success:", result);
        toast({ 
          title: "Error inesperado", 
          description: "La respuesta del servidor fue incorrecta. Intenta de nuevo.", 
          variant: "destructive" 
        });
      }
    } catch (err) {
      console.error("Error completing reset:", err);
      toast({ title: "Error de conexión", description: "No se pudo conectar con el servidor. Verifica tu conexión.", variant: "destructive" });
    } finally {
      setResetLoading(false);
    }
  };

  const toggleTask = (key: string) => {
    const newTasks = { ...detailedTasks, [key]: !detailedTasks[key] };
    setDetailedTasks(newTasks);
    if (user) {
      localStorage.setItem(`protocol_tasks_${user.id}`, JSON.stringify(newTasks));
    }
  };

  const markDayCompleted = async (dayIdx: number) => {
    if (!user) return;
    
    // Check if essential task is done
    const essentialKey = `${dayIdx}-essential`;
    if (!detailedTasks[essentialKey]) {
      toast({ title: "Atención", description: "Debes completar al menos la tarea esencial para avanzar.", variant: "destructive" });
      return;
    }

    const newCompletedDays = new Set(completedDays);
    const isNewCompletion = !newCompletedDays.has(dayIdx);
    
    if (isNewCompletion) {
      newCompletedDays.add(dayIdx);
      setCompletedDays(newCompletedDays);
      
      try {
        // Specialized proxy handles the check and upsert internally
        const resUpdate = await fetch('/api/protocol/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            day_number: dayIdx,
            completed: true
          })
        });

        const { error } = await resUpdate.json();
        if (error) throw error;

        // Celebrate
        if (!celebratedDays.current.has(dayIdx)) {
          celebratedDays.current.add(dayIdx);
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
          toast({ title: "¡Felicidades!", description: "Día completado con éxito." });
        }
      } catch (err) {
        console.error("Error updating completion:", err);
      }
    }
    
    setSelectedDay(null); // Go back to list
  };


  const getCompletionPercent = () => Math.round((completedDays.size / 21) * 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  // ==== DETAIL VIEW ====
  if (selectedDay !== null) {
    const dayData = protocolDays[selectedDay];
    const essentialKey = `${selectedDay}-essential`;
    const isEssentialDone = detailedTasks[essentialKey] || false;
    const persFocus = getPersonalizationFocus(dayData);

    return (
      <div className="p-4 md:p-8 max-w-lg mx-auto space-y-6 animate-fade-in pb-24 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => setSelectedDay(null)} className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </button>
          <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
            Día {dayData.day} de 21
          </span>
        </div>

        <div>
          <h1 className="text-3xl font-heading font-bold mt-2 leading-tight">{dayData.title}</h1>
          <p className="text-lg text-muted-foreground mt-1">{dayData.subtitle}</p>
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground bg-card p-3 rounded-xl border">
          <div className="flex items-center gap-1.5"><Target className="h-4 w-4 text-primary" /> {dayData.estimated_minutes} min</div>
          <div className="w-px h-4 bg-border"></div>
          <div className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-wellness-peach" /> {dayData.difficulty}</div>
        </div>

        {persFocus && (
          <div className="bg-wellness-peach/20 text-wellness-peach-dark px-4 py-3 rounded-xl text-sm font-medium border border-wellness-peach/30">
            {persFocus}
          </div>
        )}

        {/* Why it matters */}
        <div className="space-y-4">
          <div className="bg-secondary rounded-2xl p-5 border border-primary/10">
            <h3 className="font-semibold flex items-center gap-2 mb-2 text-primary-dark">
              <Info className="h-4 w-4" /> ¿Por qué es importante?
            </h3>
            <p className="text-sm text-secondary-foreground leading-relaxed">{dayData.why_this_matters}</p>
          </div>

          <div className="bg-card shadow-soft rounded-2xl p-5 border">
            <h3 className="font-semibold text-foreground mb-2">Qué esperar hoy</h3>
            <p className="text-sm text-muted-foreground">{dayData.expected_outcome}</p>
          </div>
        </div>

        <div className="w-full h-px bg-border my-6"></div>

        {/* Tasks */}
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Star className="h-5 w-5 text-primary fill-primary" /> Tarea Esencial
            </h2>
            <button
              onClick={() => toggleTask(essentialKey)}
              className={cn(
                "w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all",
                isEssentialDone ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
              )}
            >
              <div className={cn(
                "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
                isEssentialDone ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"
              )}>
                {isEssentialDone && <Check className="h-3 w-3" />}
              </div>
              <div>
                <p className={cn("text-base font-medium", isEssentialDone && "text-muted-foreground line-through")}>
                  {dayData.essential_task}
                </p>
              </div>
            </button>
          </div>

          <div>
            <h2 className="text-lg font-bold mb-3 text-muted-foreground">Tareas Recomendadas (Para el 100%)</h2>
            <div className="space-y-3">
              {dayData.recommended_tasks.map((task, idx) => {
                const recKey = `${selectedDay}-rec-${idx}`;
                const isRecDone = detailedTasks[recKey];
                return (
                  <button
                    key={idx}
                    onClick={() => toggleTask(recKey)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all",
                      isRecDone ? "border-wellness-peach bg-wellness-peach/5" : "border-border bg-card"
                    )}
                  >
                    <div className={cn(
                      "w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0",
                      isRecDone ? "border-wellness-peach bg-wellness-peach text-white" : "border-muted-foreground"
                    )}>
                      {isRecDone && <Check className="h-3 w-3" />}
                    </div>
                    <p className={cn("text-sm", isRecDone && "text-muted-foreground line-through")}>{task}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-muted/50 p-4 rounded-xl border border-border/50">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">💡 Tip Práctico</p>
            <p className="text-sm italic text-foreground">{dayData.practical_tip}</p>
          </div>

          {dayData.reflection_question && (
            <div className="pt-4 pb-2">
              <p className="text-sm font-medium text-center text-muted-foreground mb-3">{dayData.reflection_question}</p>
            </div>
          )}

          <div className="pt-4 pb-8">
            <Button 
              size="lg" 
              className="w-full h-14 rounded-xl text-lg font-medium shadow-primary/20 shadow-lg"
              onClick={() => markDayCompleted(selectedDay)}
            >
              {isEssentialDone ? "Marcar Día Completado" : "Completa la tarea esencial primero"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==== RESET GATE VIEW ====
  if (!isResetCompleted) {
    return (
      <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-8 animate-fade-in pb-24">
        <div className="bg-gradient-to-br from-primary/10 to-wellness-peach/10 border border-primary/20 rounded-3xl p-8 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 space-y-6">
            <div className="w-20 h-20 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mx-auto shadow-lg rotate-3">
              <Lock className="h-10 w-10" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-3xl font-heading font-bold">¡Tu Jornada Comienza Aquí!</h1>
              <p className="text-muted-foreground text-lg max-w-md mx-auto">
                Para desbloquear el protocolo de 21 días, primero debes completar el <strong>RESET DE 3 DÍAS</strong>.
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur-sm border rounded-2xl p-6 space-y-4 max-w-sm mx-auto">
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-full bg-wellness-peach/20 text-wellness-peach-dark flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold text-sm">Método Reset 3 Días</p>
                  <p className="text-xs text-muted-foreground">Guía completa en PDF</p>
                </div>
              </div>
              
              <Button asChild variant="outline" className="w-full h-12 rounded-xl">
                <a href="/Metodo-Reset-3-Dias.pdf" target="_blank" rel="noopener noreferrer">
                  Ver Guía Reset (PDF)
                </a>
              </Button>
            </div>

            <div className="pt-4">
              <Button 
                onClick={handleCompleteReset} 
                disabled={resetLoading}
                className="w-full max-w-sm h-14 rounded-xl text-lg font-bold shadow-lg shadow-primary/20"
              >
                {resetLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "He terminado el Reset de 3 Días"
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-4 italic">
                Al completar el reset, tendrás acceso inmediato a la Fase 1 del protocolo.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-secondary/40 p-5 rounded-2xl border border-border/50">
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> ¿Por qué el Reset?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              El reset prepara tu intestino eliminando inflamación rápida y preparando tu microbiota para los hábitos de los próximos 21 días.
            </p>
          </div>
          <div className="bg-secondary/40 p-5 rounded-2xl border border-border/50">
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-wellness-peach" /> ¿Qué sigue?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Tras los 3 días, iniciaremos la Fase 1: Reducción de Hinchazón, donde consolidarás la ligereza alcanzada en el reset.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==== MAIN LIST VIEW ====
  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-8 animate-fade-in pb-24 overflow-x-hidden">
      {/* Hero Card */}
      <div className="bg-card border rounded-3xl p-6 shadow-card relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-primary mb-1">Fase {currentPhase} de 3</p>
              <h1 className="text-2xl font-heading font-bold text-foreground">Tu Jornada</h1>
            </div>
            <div className="w-14 h-14 rounded-full border-4 border-primary/20 flex items-center justify-center relative">
              <svg className="w-full h-full absolute -rotate-90">
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-transparent" />
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-primary" strokeDasharray="138" strokeDashoffset={138 - (138 * getCompletionPercent()) / 100} strokeLinecap="round" />
              </svg>
              <span className="text-xs font-bold">{getCompletionPercent()}%</span>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="bg-secondary rounded-xl p-3 flex-1">
              <p className="text-xs text-muted-foreground mb-1">Adherencia</p>
              <p className="text-lg font-bold flex items-center gap-1.5"><Trophy className="h-4 w-4 text-wellness-peach" /> {completedDays.size} días</p>
            </div>
            <div className="bg-secondary rounded-xl p-3 flex-1">
              <p className="text-xs text-muted-foreground mb-1">Próxima meta</p>
              <p className="text-lg font-bold flex items-center gap-1.5"><Target className="h-4 w-4 text-primary" /> Día {currentPhase * 7}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Days List Grouped by Phase */}
      <div className="space-y-10">
        {[1, 2, 3].map(phaseNum => {
          const phaseDays = protocolDays.filter(d => d.phase === phaseNum);
          const isPhaseUnlocked = unlockedDays >= (phaseNum - 1) * 7;
          
          if (!isPhaseUnlocked) return null;

          return (
            <div key={phaseNum} className="space-y-4">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{phaseNum}</span>
                  {phaseNum === 1 ? "Reducir Hinchazón" : phaseNum === 2 ? "Mejorar Ritmo" : "Consolidar Hábitos"}
                </h2>
              </div>
              
              <div className="space-y-3">
                {phaseDays.map((day) => {
                  const locked = day.day - 1 >= unlockedDays;
                  const allDone = completedDays.has(day.day - 1);
                  const isCurrentDay = day.day - 1 === maxCompleted + 1;

                  return (
                    <button
                      key={day.day}
                      onClick={() => !locked && setSelectedDay(day.day - 1)}
                      disabled={locked}
                      className={cn(
                        "w-full flex items-center gap-4 px-4 py-4 rounded-2xl border transition-all text-left relative overflow-hidden",
                        locked ? "opacity-50 border-border bg-muted" : "border-border bg-card shadow-card hover:border-primary/40",
                        allDone && "border-primary/20 bg-secondary/50",
                        isCurrentDay && "border-primary shadow-primary/10 shadow-lg"
                      )}
                    >
                      {isCurrentDay && <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary rounded-l-2xl"></div>}
                      
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold transition-all",
                        allDone ? "gradient-primary text-primary-foreground shadow-sm" : 
                        locked ? "bg-muted-foreground/10 text-muted-foreground" : 
                        "bg-primary/10 text-primary"
                      )}>
                        {allDone ? <Check className="h-5 w-5" /> : locked ? <Lock className="h-4 w-4" /> : day.day}
                      </div>
                      
                      <div className="flex-1 min-w-0 py-1">
                        <p className={cn("text-base font-semibold leading-tight mb-1", allDone ? "text-foreground" : "text-foreground")}>{day.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{day.objective}</p>
                      </div>
                      
                      {!locked && (
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          {isCurrentDay && <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Hoy</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Milestone Card */}
              {phaseNum === 1 && completedDays.has(6) && (
                <div className="bg-wellness-peach/10 border border-wellness-peach/20 rounded-2xl p-5 text-center mt-6">
                  <h3 className="text-wellness-peach-dark font-bold mb-1">🎉 ¡Semana 1 Completada!</h3>
                  <p className="text-sm text-wellness-peach-dark/80">Has dado el primer gran paso. Tu cuerpo ya se está adaptando a la ligereza.</p>
                </div>
              )}
              {phaseNum === 2 && completedDays.has(13) && (
                <div className="bg-primary/10 border border-primary/20 rounded-2xl p-5 text-center mt-6">
                  <h3 className="text-primary-dark font-bold mb-1">🌟 ¡Dos Semanas!</h3>
                  <p className="text-sm text-primary-dark/80">La consistencia está dando frutos. Ya no es una dieta, es tu estilo de vida.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Protocol;